import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CalendarIcon, HandIcon, HeartIcon, MapPinIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { LOCATIONS, OPENING_HOURS } from '../data/locations';
import { LocationCard } from '../components/LocationCard';
import { PricingMenus } from '../components/PricingMenus';
import { SectionHeading } from '../components/SectionHeading';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/11ae6ca3-d026-4d2e-a01d-91562f27ed20.jpg';
const STATS = [{
  icon: MapPinIcon,
  value: '9',
  label: 'Locations'
}, {
  icon: UsersIcon,
  value: '20+',
  label: 'Trusted Therapists'
}, {
  icon: CalendarIcon,
  value: '300+',
  label: 'Weekly Bookings'
}];
const FEATURES = [{
  icon: HandIcon,
  title: 'Skilled hands',
  body: 'Our therapists draw on traditional techniques from across East and Southeast Asia, refined over years of practice.'
}, {
  icon: HeartIcon,
  title: 'Private & discreet',
  body: 'Low light, soft music, a room to yourself. Every visit stays between you and your therapist.'
}, {
  icon: SparklesIcon,
  title: 'Open late, seven days',
  body: `${OPENING_HOURS}. Walk in or book ahead — whatever suits your evening.`
}];
export function Home() {
  return <main className="w-full bg-black" data-id="element-217" data-mp="2mwqrraz6984t">
      <section className="relative w-full overflow-hidden" data-id="element-218" data-mp="2hxnljn7ggvv9">
        <img src={HERO_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-219" data-mp="2hz8h9g5iakup" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black" data-id="element-220" data-mp="2895o9fydx6wd" />
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative mx-auto flex w-full max-w-4xl flex-col items-center px-5 py-32 text-center sm:px-8 sm:py-44" data-id="element-221" data-mp="23a2gvouxmol8">
          <span className="rounded-full border border-lotus-500/40 bg-black/40 px-5 py-2 text-[11px] font-medium uppercase tracking-[0.32em] text-lotus-400 backdrop-blur" data-id="element-222" data-mp="1kjnvx6fay9y1">
            Men only · 18+
          </span>

          <h1 className="text-shadow-soft mt-10 font-display text-5xl font-light leading-[1.05] tracking-wide text-white sm:text-7xl" data-id="element-223" data-mp="1ao42qrs4hpcz">
            Slow down.
            <br data-id="element-224" data-mp="23aouvugfa5hl" />
            <em className="italic text-lotus-500" data-id="element-225" data-mp="23c9nvfn39gdc">This hour is yours.</em>
          </h1>

          <div className="rule-gold mt-8 w-32" data-id="element-226" data-mp="28akxpqddvqvk" />

          <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-neutral-300" data-id="element-227" data-mp="28ce5xa3naj5o">
            Nine private studios across Brisbane and the Gold Coast. Warm oil, dim light and
            therapists who know exactly where the tension sits.
          </p>

          <div className="mt-12 flex flex-col gap-4 sm:flex-row" data-id="element-228" data-mp="2m91de6t84rpu">
            <Link to="/book" className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-lotus-500 px-10 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-black transition-colors hover:bg-lotus-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-lotus-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-229" data-mp="2r0j7qc0qru1u">
              Book Now
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-230" data-mp="kdliaku76tu8" />
            </Link>
            <Link to="/locations" className="inline-flex items-center justify-center rounded-full border border-white/20 px-10 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white backdrop-blur transition-colors hover:border-lotus-500 hover:text-lotus-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-231" data-mp="3ntslkq8pap2h">
              Find a Studio
            </Link>
          </div>
        </motion.div>
      </section>

      <section aria-label="Key figures" className="w-full border-y border-white/5 bg-ink-950" data-id="element-232" data-mp="27fg30kow7y2a">
        <div className="mx-auto grid w-full max-w-5xl divide-y divide-white/5 px-5 sm:px-8 md:grid-cols-3 md:divide-x md:divide-y-0" data-id="element-233" data-mp="2cdhvtxc4fbqy">
          {STATS.map((stat, i) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.08,
          ease: 'easeOut'
        }} className="flex flex-col items-center py-12 text-center" data-id="element-234" data-mp="2mbfi4faf6ygx" data-mpi={i}>
              <stat.icon className="h-5 w-5 text-lotus-500/70" aria-hidden="true" data-id="element-235" data-mp="351ne8dfpconm" />
              <span className="mt-4 font-display text-5xl font-light text-lotus-500" data-id="element-236" data-mp="353hq728zx6fk">
                {stat.value}
              </span>
              <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.28em] text-neutral-500" data-id="element-237" data-mp="3e1k067apj9ez">
                {stat.label}
              </p>
            </motion.div>)}
        </div>
      </section>

      <section aria-labelledby="why-heading" className="glow-warm w-full bg-black" data-id="element-238" data-mp="ycbsyb6nm87e">
        <div className="mx-auto w-full max-w-7xl px-5 py-24 sm:px-8" data-id="element-239" data-mp="tdicc9w2m130">
          <SectionHeading id="why-heading" eyebrow="The Experience" title="Why Me Time" subtitle="A consistent, unhurried experience at every studio across Brisbane and the Gold Coast." data-id="element-240" data-mp="of73a1dlnulu" />
          <div className="mt-16 grid gap-6 md:grid-cols-3" data-id="element-241" data-mp="1h579c3ju7dqa">
            {FEATURES.map((feature, i) => <motion.article key={feature.title} initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.08,
            ease: 'easeOut'
          }} className="group rounded-2xl border border-white/5 bg-ink-850/80 p-8 transition-colors hover:border-lotus-500/40" data-id="element-242" data-mp="1cbl5n0vnik6m" data-mpi={i}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-lotus-500/30 text-lotus-500 transition-colors group-hover:bg-lotus-500/10" data-id="element-243" data-mp="26jrxl3ukgrf">
                  <feature.icon className="h-5 w-5" aria-hidden="true" data-id="element-244" data-mp="3kiydgla773cb" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-light tracking-wide text-white" data-id="element-245" data-mp="3phj3xszne8nk">
                  {feature.title}
                </h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-neutral-400" data-id="element-246" data-mp="3fmr5yo25ljs9">
                  {feature.body}
                </p>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section aria-labelledby="pricing-heading" className="w-full bg-ink-950" data-id="element-247" data-mp="36l9tdfnyg08b">
        <div className="mx-auto w-full max-w-5xl px-5 py-24 sm:px-8" data-id="element-248" data-mp="zvv7qcc1a53y">
          <PricingMenus data-id="element-249" data-mp="14u6f8geuw3j0" />
        </div>
      </section>

      <section aria-labelledby="locations-heading" className="glow-warm w-full bg-black" data-id="element-250" data-mp="14rka0nffu03y">
        <div className="mx-auto w-full max-w-7xl px-5 py-24 sm:px-8" data-id="element-251" data-mp="1extme39tgl21">
          <SectionHeading id="locations-heading" eyebrow="Our Studios" title="Find your room" subtitle="Nine venues across Brisbane and the Gold Coast, open seven days until late." data-id="element-252" data-mp="19zjuc1je20kn" />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-253" data-mp="1ixc6wxxq6u4c">
            {LOCATIONS.map((location, i) => <LocationCard key={location.slug} location={location} index={i} data-id="element-254" data-mp="1sp8gb559i6nv" data-mpi={i} />)}
          </div>
        </div>
      </section>

      <section className="relative w-full overflow-hidden bg-ink-950" data-id="element-255" data-mp="26ngchauh53xz">
        <div className="glow-warm absolute inset-0" data-id="element-256" data-mp="22mmji326cyy8" />
        <div className="relative mx-auto w-full max-w-3xl px-5 py-28 text-center sm:px-8" data-id="element-257" data-mp="22l1rak3c1s3j">
          <h2 className="font-display text-4xl font-light tracking-wide text-white sm:text-5xl" data-id="element-258" data-mp="cb070c9i5tu6">
            Ready to <em className="italic text-lotus-500" data-id="element-259" data-mp="8b7x9w8dhhn8">unwind</em>?
          </h2>
          <div className="rule-gold mx-auto mt-6 w-24" data-id="element-260" data-mp="wtjx24gchi71" />
          <p className="mt-6 font-light text-neutral-400" data-id="element-261" data-mp="11swfmd06o33t">
            Choose your studio and book a session in under a minute.
          </p>
          <Link to="/book" className="group mt-10 inline-flex items-center justify-center gap-2.5 rounded-full bg-lotus-500 px-10 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-black transition-colors hover:bg-lotus-600" data-id="element-262" data-mp="16kekhc5bunu2">
            Book Now
            <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-263" data-mp="iv3gllsokdbp" />
          </Link>
        </div>
      </section>
    </main>;
}